import { useCallback, useEffect, useState } from "react";

// ПОЯВЛЕНИЕ БЛОКА ПРИ ПРОКРУТКЕ.
//
// Возвращает ref для элемента и класс. Наблюдатель отключается после первого
// срабатывания — блок не должен мигать, если листать вверх-вниз.
//
// ГЛАВНОЕ ПРАВИЛО ЭТОГО ХУКА: текст важнее анимации. Блок стартует
// прозрачным, поэтому любая осечка означает не «нет анимации», а «на сайте
// пропал целый раздел».
//
// ПОЧЕМУ ЗДЕСЬ CALLBACK-REF, А НЕ useRef.
// Именно на этом хук и ломался. Блок «О нас» наполняется из админки и, пока
// данные не пришли, возвращает null. Порядок был такой:
//   1. секция монтируется → рендерит null → ref.current === null;
//   2. эффект хука отрабатывает, видит пустой ref и молча выходит;
//   3. данные приходят, секция наконец рисуется…
//   4. …но эффект больше не запускается: его зависимости не изменились.
// Наблюдателя не создавалось никогда, класс reveal-in не появлялся, и текст
// «О нас» навсегда оставался с opacity: 0 — со стороны выглядело так, будто
// текста на сайте нет вовсе.
//
// Callback-ref решает это в корне: React зовёт его в тот момент, когда узел
// реально появляется в DOM, и хук подписывается именно тогда, а не «в
// надежде, что к первому эффекту всё уже отрисовалось».
//
// Остальные страховки:
//   · threshold: 0 — хватает одного видимого пикселя. При 0.15 блок выше
//     экрана мог не набрать нужной доли площади вообще никогда;
//   · если узел уже в окне (открыли страницу на середине, браузер
//     восстановил прокрутку) — показываем сразу, не дожидаясь наблюдателя;
//   · нет IntersectionObserver — показываем сразу.
//
// Глухого таймера «через N секунд показать всё» здесь нарочно нет: он бы
// проявлял и те блоки, до которых человек ещё не долистал.

export default function useReveal({
  threshold = 0,
  // Небольшой отрицательный отступ снизу: блок проявляется, когда край уже
  // немного зашёл в экран, а не ровно в момент касания.
  rootMargin = "0px 0px -40px 0px",
} = {}) {
  const [node, setNode] = useState(null);
  const [shown, setShown] = useState(false);

  // Стабильная функция: React вызовет её с узлом при монтировании и с null
  // при размонтировании.
  const ref = useCallback((element) => setNode(element), []);

  useEffect(() => {
    if (!node || shown) return;

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }

    // Узел уже на экране — показываем немедленно.
    const rect = node.getBoundingClientRect();
    const viewport = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < viewport && rect.bottom > 0) {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [node, shown, threshold, rootMargin]);

  return { ref, className: shown ? "reveal reveal-in" : "reveal" };
}
